import {Injectable } from '@angular/core';
import { Router } from '@angular/router';
import {FetchPolicy} from '@apollo/client/core';

import { of } from 'rxjs';
import { catchError, distinctUntilChanged, filter, map, switchMap } from 'rxjs/operators';

import { AttentionItem, AttentionItemsSummaryGQL, AttentionItemsTotalGQL, ConfigValue, GetConfigValuesGQL, Job, ZoneDir } from '../../generated/graphql.generated';
import { safeParseJSON } from '../js';
import { BrandingService } from '../services/branding.service';
import { DetailsHelperService } from '../services/details-helper.service';
import { DocumentHelperService } from '../services/document-helper.service';
import { EventHelperService } from '../services/event-helper.service';
import { FreyaNotificationsService } from '../services/freya-notifications.service';

const DEFAULT_OUTSTANDING_BALANCE_EXPLANATION = 'invoice-outstanding-balance';
const DEFAULT_LONG_OVERDUE_OUTSTANDING_BALANCE_EXPLANATION = 'long-overdue-invoice-outstanding-balance';
export interface AttentionConfig {
  action: string;
  attentionExplanation: string;
  label: string;
  description?: string;
  score: number;
}

@Injectable({
  providedIn: 'root'
})
export class AttentionItemsService {

  invoiceOutstandingBalance = DEFAULT_OUTSTANDING_BALANCE_EXPLANATION;
  longOverdueInvoiceOutstandingBalance = DEFAULT_LONG_OVERDUE_OUTSTANDING_BALANCE_EXPLANATION;

  configs$ = this.branding.currentZone()
    .pipe(
      filter((zone) => Boolean(zone)),
      distinctUntilChanged((prev, curr) => prev?.id === curr?.id),
      switchMap(() => this.getConfigValuesGQL.fetch({ keys: [ 'attention.*' ] })
      .pipe(
        filter((res) => Boolean(res.data?.getConfigValues)),
        map((res) => {
          const configValues = res.data.getConfigValues;
          const configs = configValues.map((c) => safeParseJSON(c.value)) as AttentionConfig[];
          this.setOutstandingBalanceExplanations(configValues);
          return configs as AttentionConfig[];
        }),
      )
    ));

summaries$ = this.configs$
    .pipe(
        switchMap((configs) => this.attentionItemsSummaryGQL
            .fetch({ filter: { zoneDir: ZoneDir.Lte } })
            .pipe(
                map((res) => {
                    if (res.loading) {
                        return {
                            loading: true,
                            summaries: [],
                        };
                    }

                    const summaries = res.data?.attentionItemsSummary.map((s: { attentionExplanation: string; items: any }) => {
                        const config = configs.find((c) => c.attentionExplanation === s.attentionExplanation);

                        return {
                            attentionExplanation: config?.attentionExplanation,
                            label: config?.label,
                            items: s.items,
                        };
                    }) || [];

                    return { loading: false, summaries };
                }),
                catchError((error) => {
                    console.error('Error fetching attention items summary:', error);
                    return of({
                        loading: false,
                        summaries: [],
                        error: 'Failed to fetch attention items summary',
                    });
                })
            )));

  constructor(
    private localNotify: FreyaNotificationsService,
    private router: Router,
    private eventHelperService: EventHelperService,
    private detailsHelper: DetailsHelperService,
    private getConfigValuesGQL: GetConfigValuesGQL,
    private branding: BrandingService,
    private attentionItemsSummaryGQL: AttentionItemsSummaryGQL,
    private attentionItemsTotalGQL: AttentionItemsTotalGQL,
    private documentHelper: DocumentHelperService,
  ) { }

  getConfigs() {
    const attentionConfigs$ = this.getConfigValuesGQL.fetch({ keys: [ 'attention.*' ] })
      .pipe(
        filter((res) => Boolean(res.data?.getConfigValues)),
        map((res) => res.data.getConfigValues.map((c) => safeParseJSON(c.value))),
      );

    return this.branding.currentZone().pipe(switchMap(() => attentionConfigs$));
  }

  async performItemAction(item: AttentionItem, afterCb?: () => void) {

    switch(item.action) {
      case 'process-job':
        if (item.__typename === 'Job') {
          const isOutstanding = this.isAnOutstandingBalanceItem(item);
          const tab = isOutstanding ? 'billing' : 'overview';
          this.router.navigate([ '/jobs', item.id, tab ]);
        } else {
          this.localNotify.error('Action not implemented for this type of item');
        }
        break;
      case 'generate-invoice':
        if (item.__typename === 'Job') {
          this.documentHelper.openCreateInvoiceDialog(item.id);
        } else {
          this.localNotify.error('Action not implemented for this type of item');
        }
        break;
      case 'complete-event':
        if (item.__typename === 'CalendarEvent') {
          await this.eventHelperService.updateEventStatus(item.id, 'completed')
            .catch(() => this.localNotify.error('Could not update event status'));

          if (!afterCb) { return; };

          afterCb();
        } else {
          this.localNotify.error('Action not implemented for this type of item');
        }
        break;
      case 'book-event':
        if (item.__typename === 'Job') {
          this.goToBooking(item);
        } else {
          this.localNotify.error('Action not implemented for this type of item');
        }
        break;
      default:
        this.localNotify.error('Action not implemented');
    }
  };

  goToBooking(job: Job) {
    const uncompletedEvents = job.events
      .filter((e) => e.status !== 'completed')
      .sort((a, b) => a.start - b.start);

    const [ first ] = uncompletedEvents;

    const queryParams = first ? { workorderId: first.id } : {};

    this.router.navigate([ 'jobs', job.id, 'workorders' ], { queryParams });
  }

  openItem(item: AttentionItem) {
    this.detailsHelper.open(this.getDetailsItemType(item), { id: item.id });
  }

  fetchTotal(fetchPolicy: FetchPolicy) {
    return this.attentionItemsTotalGQL.fetch({}, { fetchPolicy })
      .pipe(
        filter(result => !result.loading),
        map(result => result.data.attentionItems.total)
      );
  }

  getDetailsItemType(item: AttentionItem) {
    switch (item.__typename) {
      case 'Job':
        return 'job';
      case 'CalendarEvent':
        return 'calendar-event';
      case 'User':
       return 'users';
    }
  }

  setOutstandingBalanceExplanations(configs: ConfigValue[]) {
    const setExplanation = (key: string, defaultExplanation: string) => {
      const config = configs.find((c) => c.key === key);
      if (config) {
        const value = safeParseJSON(config.value);
        return value.attentionExplanation || defaultExplanation;
      }
      return defaultExplanation;
    };

    this.invoiceOutstandingBalance = setExplanation('attention.invoiceOutstandingBalance', DEFAULT_OUTSTANDING_BALANCE_EXPLANATION);
    this.longOverdueInvoiceOutstandingBalance = setExplanation('attention.longOverdueInvoiceOutstandingBalance',
    DEFAULT_LONG_OVERDUE_OUTSTANDING_BALANCE_EXPLANATION);
  }

  isAnOutstandingBalanceItem(item: AttentionItem) {
    return item.attentionExplanation.includes(this.invoiceOutstandingBalance)
    || item.attentionExplanation.includes(this.longOverdueInvoiceOutstandingBalance);
  }

}
